import React, { useEffect, useState } from 'react';
import { WorkplaceDocumentItem, archiveApi } from '../../services/api';

interface DocumentPreviewModalProps {
  document: WorkplaceDocumentItem | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload: (doc: WorkplaceDocumentItem) => void;
}

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  document,
  isOpen,
  onClose,
  onDownload
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let activeUrl: string | null = null;
    if (isOpen && document) {
      setLoading(true);
      setError(false);
      archiveApi.previewDocument(document.id)
        .then((url) => {
          activeUrl = url;
          setBlobUrl(url);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Önizleme yüklenemedi:', err);
          setError(true);
          setLoading(false);
        });
    }

    return () => {
      if (activeUrl) {
        window.URL.revokeObjectURL(activeUrl);
      }
      setBlobUrl(null);
    };
  }, [isOpen, document]);

  if (!isOpen || !document) return null;

  const ext = (document.fileName || document.originalFileName)?.split('.').pop()?.toLowerCase() || '';
  const isPdf = ext === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'heic', 'heif'].includes(ext);
  const token = localStorage.getItem('uzman_token') || localStorage.getItem('field_uzman_token');
  const directUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1'}/archive/${document.id}/preview${token ? `?token=${token}` : ''}`;
  const displayUrl = blobUrl || directUrl;
  const workplaceTitle = document.workplace?.title || document.Workplace?.title || '';

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <div className="preview-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="preview-modal-header">
          <div className="preview-header-left">
            <span className="preview-header-icon">{isPdf ? '📕' : isImage ? '🖼️' : '📄'}</span>
            <div className="preview-title-block">
              <h3 className="preview-title" title={document.originalFileName}>
                {document.originalFileName}
              </h3>
              <span className="preview-subtitle">
                {workplaceTitle ? `🏢 ${workplaceTitle} • ` : ''}{document.documentDate || document.uploadedDate}
              </span>
            </div>
          </div>

          <div className="preview-header-actions">
            <a
              href={directUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="preview-download-btn"
              style={{ textDecoration: 'none', background: '#3b82f6', color: '#fff' }}
              title="Yeni Pencerede Aç"
            >
              🔗 Yeni Sekmede Aç
            </a>
            <button
              type="button"
              className="preview-download-btn"
              onClick={() => onDownload(document)}
              title="Bilgisayara İndir"
            >
              📥 İndir
            </button>
            <button
              type="button"
              className="preview-close-btn"
              onClick={onClose}
              title="Kapat"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="preview-modal-body">
          {loading && (
            <div className="preview-loading-state">
              <div className="preview-spinner"></div>
              <p>Belge önizlemesi hazırlanıyor...</p>
            </div>
          )}

          {error && (
            <div className="preview-error-state">
              <div className="preview-error-icon">⚠️</div>
              <h4>Önizleme Yüklenemedi</h4>
              <p>Bu dosya türü doğrudan tarayıcıda görüntülenemiyor olabilir.</p>
              <button
                type="button"
                className="preview-error-btn"
                onClick={() => onDownload(document)}
              >
                📥 Doğrudan İndirerek Görüntüle
              </button>
            </div>
          )}

          {!loading && !error && blobUrl && (
            <div className="preview-content-area">
              {isPdf && (
                <iframe
                  src={blobUrl}
                  title="PDF Önizleme"
                  className="preview-iframe"
                />
              )}
              {isImage && (
                <div className="preview-image-container">
                  <img src={blobUrl} alt={document.originalFileName} className="preview-image" />
                </div>
              )}
              {!isPdf && !isImage && (
                <div className="preview-unsupported-state">
                  <div className="unsupported-icon">📘</div>
                  <h4>{document.originalFileName}</h4>
                  <p>Word ve Excel dosyaları güvenlik standartları gereği yerel ofis yazılımı ile açılır.</p>
                  <button
                    type="button"
                    className="preview-download-btn large"
                    onClick={() => onDownload(document)}
                  >
                    📥 Dosyayı İndir ve Aç
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
